/**
 * Search Modal Component
 *
 * Global search modal accessible via keyboard shortcut (⌘K).
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search, X, FileText, ClipboardList, Users, ArrowRight } from 'lucide-react';
import { useUIStore } from '@/state/stores/ui.store';
import { MODAL_IDS } from '../../types';

/**
 * Quick action shortcuts for search
 */
const QUICK_ACTIONS = [
  {
    id: 'policies',
    label: 'Go to Policies',
    icon: FileText,
    path: '/policies',
  },
  {
    id: 'claims',
    label: 'Go to Claims',
    icon: ClipboardList,
    path: '/claims',
  },
  {
    id: 'customers',
    label: 'Go to Customers',
    icon: Users,
    path: '/customers',
  },
];

export const SearchModal = () => {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const { isModalOpen, closeModal } = useUIStore();
  const isOpen = isModalOpen(MODAL_IDS.SEARCH);

  const [query, setQuery] = useState('');

  const handleClose = useCallback(() => {
    closeModal(MODAL_IDS.SEARCH);
    setQuery('');
  }, [closeModal]);

  const handleNavigate = (path: string) => {
    navigate(path);
    handleClose();
  };

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) {
          handleClose();
        } else {
          // Open modal via store
          useUIStore.getState().openModal(MODAL_IDS.SEARCH);
        }
      }

      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50 animate-fadeIn"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4">
        <div
          className="w-full max-w-xl bg-white rounded-xl shadow-2xl animate-scaleUp overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Search"
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 border-b border-neutral-200">
            <Search className="w-5 h-5 text-neutral-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('header.searchPlaceholder')}
              className="flex-1 py-4 text-base text-neutral-900 placeholder-neutral-400 bg-transparent border-none outline-none"
              autoFocus
            />
            <button
              onClick={handleClose}
              className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Quick Actions */}
          <div className="p-2">
            <p className="px-3 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">
              Quick Actions
            </p>
            <div className="space-y-1">
              {QUICK_ACTIONS.map((action) => {
                const Icon = action.icon;

                return (
                  <button
                    key={action.id}
                    onClick={() => handleNavigate(action.path)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-neutral-700 hover:bg-neutral-100 transition-colors group"
                  >
                    <Icon className="w-5 h-5 text-neutral-400" />
                    <span className="flex-1 text-left text-sm font-medium">
                      {action.label}
                    </span>
                    <ArrowRight className="w-4 h-4 text-neutral-300 group-hover:text-neutral-500 transition-colors" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-neutral-50 border-t border-neutral-200">
            <div className="flex items-center justify-between text-xs text-neutral-500">
              <span>Press <kbd className="px-1.5 py-0.5 bg-neutral-200 rounded">ESC</kbd> to close</span>
              <span>
                <kbd className="px-1.5 py-0.5 bg-neutral-200 rounded">⌘</kbd>
                <kbd className="ml-1 px-1.5 py-0.5 bg-neutral-200 rounded">K</kbd>
                {' '}to toggle
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchModal;
